/**
 * CreativePreviewPage — list of active shareable creative-preview links.
 *
 * Features:
 *  - Create preview from a creative id (+ optional expiry in minutes).
 *  - Table of active previews: creative, shareable URL (+ copy button),
 *    expires-at, created-at, row actions.
 *  - Regenerate link action (revoke + create fresh token).
 *  - Revoke (delete) a token.
 *  - Click a row (or the Preview button) to open a preview iframe dialog.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
	useCreativePreviewList,
	useCreativePreviewCreate,
	useCreativePreviewDelete,
	useCreativePreviewRegenerate,
} from "./hooks";
import { buildPreviewUrl, type CreativePreviewRow } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const TH =
	"px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-800 align-middle";

function formatTimestampMs(ms: number | null | undefined): string {
	if (ms === null || ms === undefined || !Number.isFinite(ms)) return "-";
	try {
		const d = new Date(ms);
		if (Number.isNaN(d.getTime())) return "-";
		return d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
	} catch {
		return "-";
	}
}

function truncateMiddle(value: string, max = 48): string {
	if (value.length <= max) return value;
	const head = Math.ceil((max - 1) / 2);
	const tail = Math.floor((max - 1) / 2);
	return value.slice(0, head) + "..." + value.slice(value.length - tail);
}

interface CopyButtonProps {
	url: string;
	token: string;
}

function CopyButton({ url, token }: CopyButtonProps) {
	const [copied, setCopied] = useState(false);
	useEffect(() => {
		if (!copied) return;
		const t = setTimeout(() => setCopied(false), 1500);
		return () => clearTimeout(t);
	}, [copied]);

	const handleCopy = async () => {
		try {
			if (
				typeof navigator !== "undefined" &&
				navigator.clipboard?.writeText
			) {
				await navigator.clipboard.writeText(url);
			} else if (typeof document !== "undefined") {
				const ta = document.createElement("textarea");
				ta.value = url;
				ta.style.position = "fixed";
				ta.style.opacity = "0";
				document.body.appendChild(ta);
				ta.select();
				document.execCommand("copy");
				document.body.removeChild(ta);
			}
			setCopied(true);
		} catch {
			setCopied(false);
		}
	};

	return (
		<button
			type="button"
			onClick={handleCopy}
			className={BTN_GHOST}
			data-testid={`creative-preview-copy-${token}`}
			aria-label={copied ? "Copied" : "Copy shareable URL"}
		>
			{copied ? "Copied" : "Copy"}
		</button>
	);
}

interface PreviewDialogProps {
	url: string;
	token: string;
	onClose: () => void;
}

function PreviewDialog({ url, token, onClose }: PreviewDialogProps) {
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		if (typeof window !== "undefined") {
			window.addEventListener("keydown", onKey);
			return () => window.removeEventListener("keydown", onKey);
		}
		return undefined;
	}, [onClose]);

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Preview"
			data-testid="creative-preview-dialog"
			className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/60 p-4"
			onClick={onClose}
		>
			<div
				className="flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-[8px] border border-zinc-200 bg-white shadow-xl"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
					<div className="flex flex-col">
						<span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							Preview
						</span>
						<span
							className="font-mono text-xs text-zinc-700"
							data-testid="creative-preview-dialog-token"
						>
							{token}
						</span>
					</div>
					<div className="flex items-center gap-2">
						<a
							href={url}
							target="_blank"
							rel="noreferrer"
							className={BTN_GHOST}
							data-testid="creative-preview-dialog-open"
						>
							Open in new tab
						</a>
						<button
							type="button"
							onClick={onClose}
							className={BTN_GHOST}
							data-testid="creative-preview-dialog-close"
						>
							Close
						</button>
					</div>
				</div>
				<iframe
					title={`creative-preview-${token}`}
					src={url}
					className="h-full w-full flex-1 bg-zinc-50"
					data-testid="creative-preview-dialog-iframe"
				/>
			</div>
		</div>
	);
}

export function CreativePreviewPage() {
	const listQuery = useCreativePreviewList();
	const createMut = useCreativePreviewCreate();
	const regenerateMut = useCreativePreviewRegenerate();
	const deleteMut = useCreativePreviewDelete();

	const [creativeId, setCreativeId] = useState("");
	const [minutesStr, setMinutesStr] = useState("");
	const [openToken, setOpenToken] = useState<string | null>(null);
	const [pendingToken, setPendingToken] = useState<string | null>(null);

	const items: CreativePreviewRow[] = useMemo(
		() => listQuery.data?.previews ?? [],
		[listQuery.data],
	);

	const openRow = useMemo(
		() => items.find((r) => r.token === openToken) ?? null,
		[items, openToken],
	);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = creativeId.trim();
		if (!trimmed) return;
		const minutes = minutesStr.trim();
		const parsed = minutes === "" ? undefined : Number(minutes);
		const payload: { creativeId: string; expiresInMinutes?: number } = {
			creativeId: trimmed,
		};
		if (parsed !== undefined && Number.isFinite(parsed)) {
			payload.expiresInMinutes = Math.floor(parsed);
		}
		createMut.mutate(payload, {
			onSuccess: () => {
				setCreativeId("");
				setMinutesStr("");
			},
		});
	};

	const handleRegenerate = (row: CreativePreviewRow) => {
		setPendingToken(row.token);
		regenerateMut.mutate(
			{ token: row.token, creativeId: row.creative_id },
			{
				onSettled: () => setPendingToken(null),
			},
		);
	};

	const handleRevoke = (row: CreativePreviewRow) => {
		setPendingToken(row.token);
		deleteMut.mutate(row.token, {
			onSettled: () => setPendingToken(null),
		});
	};

	return (
		<main
			data-testid="creative-preview-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					creative-preview
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Creative preview links
				</h1>
				<p className="text-sm text-zinc-600">
					Mint shareable URLs for creative assets. Links expire
					automatically and can be revoked or regenerated at any
					time.
				</p>
			</header>

			<section className={CARD} aria-label="Create creative preview link">
				<h2 className="text-base font-semibold text-zinc-900">
					New preview link
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_160px_auto]"
					data-testid="creative-preview-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Creative id (e.g. cr_abc123)"
						value={creativeId}
						onChange={(e) => setCreativeId(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="creative-preview-creative-id-input"
						aria-label="Creative id"
					/>
					<input
						type="number"
						className={INPUT}
						placeholder="Expires in min (opt)"
						value={minutesStr}
						min={1}
						max={10080}
						onChange={(e) => setMinutesStr(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="creative-preview-minutes-input"
						aria-label="Expires in minutes"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending || !creativeId.trim()}
						data-testid="creative-preview-submit"
					>
						{createMut.isPending ? "Creating." : "Create link"}
					</button>
				</form>
				{createMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="creative-preview-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Active creative preview links"
			>
				<div className="flex items-baseline justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Active links
					</h2>
					<span
						className="text-xs text-zinc-500"
						data-testid="creative-preview-count"
					>
						{items.length} total
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="creative-preview-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="creative-preview-list-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="creative-preview-empty"
					>
						No active preview links yet. Create one above.
					</p>
				) : (
					<div
						className="mt-3 overflow-x-auto"
						data-testid="creative-preview-list"
					>
						<table className="w-full border-collapse">
							<thead>
								<tr className="border-b border-zinc-200">
									<th className={TH}>Creative</th>
									<th className={TH}>Shareable URL</th>
									<th className={TH}>Expires</th>
									<th className={TH}>Created</th>
									<th className={TH + " text-right"}>
										Actions
									</th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => {
									const url = buildPreviewUrl(row.token);
									const busy =
										pendingToken === row.token &&
										(regenerateMut.isPending ||
											deleteMut.isPending);
									return (
										<tr
											key={row.token}
											className="cursor-pointer border-b border-zinc-100 hover:bg-zinc-50"
											onClick={() =>
												setOpenToken(row.token)
											}
											data-testid={`creative-preview-row-${row.token}`}
										>
											<td className={TD}>
												<span className="font-mono text-xs text-zinc-700">
													{row.creative_id}
												</span>
											</td>
											<td className={TD}>
												<div
													className="flex items-center gap-2"
													onClick={(e) =>
														e.stopPropagation()
													}
												>
													<code
														className="truncate rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-1 font-mono text-xs text-zinc-700"
														title={url}
														data-testid={`creative-preview-url-${row.token}`}
													>
														{truncateMiddle(url)}
													</code>
													<CopyButton
														url={url}
														token={row.token}
													/>
												</div>
											</td>
											<td
												className={TD}
												data-testid={`creative-preview-expires-${row.token}`}
											>
												<span className="font-mono text-xs text-zinc-600">
													{formatTimestampMs(
														row.expires_at,
													)}
												</span>
											</td>
											<td className={TD}>
												<span className="font-mono text-xs text-zinc-500">
													{formatTimestampMs(
														row.created_at,
													)}
												</span>
											</td>
											<td
												className={TD + " text-right"}
												onClick={(e) =>
													e.stopPropagation()
												}
											>
												<div className="flex items-center justify-end gap-2">
													<button
														type="button"
														className={BTN_GHOST}
														onClick={() =>
															setOpenToken(
																row.token,
															)
														}
														data-testid={`creative-preview-open-${row.token}`}
													>
														Preview
													</button>
													<button
														type="button"
														className={BTN_GHOST}
														onClick={() =>
															handleRegenerate(
																row,
															)
														}
														disabled={busy}
														data-testid={`creative-preview-regenerate-${row.token}`}
													>
														{busy &&
														regenerateMut.isPending
															? "Regenerating."
															: "Regenerate"}
													</button>
													<button
														type="button"
														className={BTN_DANGER}
														onClick={() =>
															handleRevoke(row)
														}
														disabled={busy}
														data-testid={`creative-preview-revoke-${row.token}`}
													>
														{busy &&
														deleteMut.isPending
															? "Revoking."
															: "Revoke"}
													</button>
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				{regenerateMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="creative-preview-regenerate-error"
					>
						{regenerateMut.error.message}
					</p>
				) : null}
				{deleteMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="creative-preview-delete-error"
					>
						{deleteMut.error.message}
					</p>
				) : null}
			</section>

			{openRow ? (
				<PreviewDialog
					url={buildPreviewUrl(openRow.token)}
					token={openRow.token}
					onClose={() => setOpenToken(null)}
				/>
			) : null}
		</main>
	);
}

export default CreativePreviewPage;
